import React, { useState, useEffect, useRef } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { createWavFile } from '../util/wavUtils';

const { FiPlay, FiPause, FiDownload } = FiIcons;

const AudioPlayer = ({ audioData }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    if (audioData && audioData.data) {
      try {
        // Decode the base64 data properly for 16-bit PCM
        const binaryString = atob(audioData.data);
        const pcmData = new Uint8Array(binaryString.length);
        
        // Convert binary string to byte array efficiently
        for (let i = 0; i < binaryString.length; i++) {
          pcmData[i] = binaryString.charCodeAt(i);
        }

        // Audio parameters from the mime type: audio/L16;codec=pcm;rate=24000
        const sampleRate = 24000;
        const bitsPerSample = 16;
        const numChannels = 1; // Mono audio

        // Create a valid WAV Blob by adding the header
        const wavBlob = createWavFile(pcmData, sampleRate, numChannels, bitsPerSample);
        const url = URL.createObjectURL(wavBlob);
        setAudioUrl(url);

        return () => {
          URL.revokeObjectURL(url);
        };
      } catch (error) {
        console.error('Error processing audio data:', error);
      }
    }
  }, [audioData]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onEnded = () => setIsPlaying(false);
    audio.addEventListener('ended', onEnded);

    return () => {
      audio.removeEventListener('ended', onEnded);
    };
  }, [audioRef.current]);


  const togglePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleDownload = () => {
    if (audioUrl) {
      const a = document.createElement('a');
      a.href = audioUrl;
      a.download = 'podcast_summary.wav';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  if (!audioUrl) {
    return <div>Loading audio...</div>;
  }

  return (
    <div className="flex items-center gap-4 p-3 bg-slate-100 rounded-lg">
      <audio ref={audioRef} src={audioUrl} preload="auto"></audio>
      <button
        onClick={togglePlayPause}
        className="p-3 bg-green-600 text-white rounded-full hover:bg-green-700 transition-colors"
      >
        <SafeIcon icon={isPlaying ? FiPause : FiPlay} className="w-5 h-5" />
      </button>
      <div className="flex-1 text-left">
        <p className="font-semibold text-slate-800">Podcast Summary</p>
        <p className="text-sm text-slate-500">Ready to play or download.</p>
      </div>
      <button
        onClick={handleDownload}
        className="p-3 bg-white text-slate-600 rounded-full hover:bg-slate-200 transition-colors border border-slate-200"
        title="Download"
      >
        <SafeIcon icon={FiDownload} className="w-5 h-5" />
      </button>
    </div>
  );
};

export default AudioPlayer;
